import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateField } from '@/components/dateFields';
import { appDate } from '@/utils/appDateFormat';
import { useSelector } from 'react-redux';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Info } from 'lucide-react';
import type { RootState } from '../../../../../store/redux/store/store';
import { Card, CardContent } from '../../../../../components/ui/Card';
import Input from '../../../../../components/ui/Input';
import Button from '../../../../../components/ui/Button';
import { Roles } from '../../../../../models/general';
import { useGosiRates } from '../../../../../hooks/useGosiRates';
import { createGosiRate, updateGosiRate, archiveGosiRate } from '../../../../../api/Payroll/gosi';
import { getApiErrorMessage } from '../../../../../utils/apiErrors';
import type {
  GosiRateScheduleView,
  GosiRateScheduleInput,
  GosiScheme,
  GosiBranchType,
} from '../../../../../models/payroll';
import { todayLocalDateParam } from '@/utils/apiDate';

interface GosiRateSchedulePanelProps {
  onError: (msg: string) => void;
}

export const GosiRateSchedulePanel: React.FC<GosiRateSchedulePanelProps> = ({ onError }) => {
  const { t } = useTranslation();
  const role = useSelector((s: RootState) => s.user.role);
  const isOperator = role === Roles.Operator || role === 'Operator';
  const { rates, refresh, loading } = useGosiRates();
  const [editing, setEditing] = useState<GosiRateScheduleView | null>(null);
  const [creating, setCreating] = useState(false);

  const handleArchive = async (id: number) => {
    if (!window.confirm(t('settings.gosi.archive.confirmBody'))) return;
    try {
      const result = await archiveGosiRate(id);
      if (result.errors.length === 0) {
        await refresh();
      } else {
        onError(getApiErrorMessage(result, t, t('common.error', 'An error occurred.')));
      }
    } catch (e) {
      onError(getApiErrorMessage(e, t, t('common.error', 'An error occurred.')));
    }
  };

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold">{t('settings.gosi.title')}</h3>
            <p className="text-xs text-muted-foreground mt-1">{t('settings.gosi.subtitle')}</p>
          </div>
          {isOperator ? (
            <Button onClick={() => setCreating(true)} iconName="Plus" size="sm">
              {t('settings.gosi.addRow')}
            </Button>
          ) : (
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    type="button"
                    aria-label={t('settings.gosi.readOnlyHint')}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Info size={16} />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content className="bg-card border border-border rounded-md p-2 shadow-md text-xs max-w-xs z-50">
                    {t('settings.gosi.readOnlyHint')}
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          )}
        </div>

        <div className="mb-3 rounded-md bg-muted/40 border border-border/60 px-3 py-2 text-xs text-muted-foreground space-y-1">
          <p>
            <span className="font-medium text-foreground">{t('settings.gosi.legend.branchesLabel', 'Branches')}:</span>{' '}
            {t('settings.gosi.legend.branches', 'ANI = pension (Saudis only) · OH = occupational hazards (all employees, employer-only) · SANED = unemployment (Saudis only).')}
          </p>
          <p>
            <span className="font-medium text-foreground">{t('settings.gosi.legend.schemeLabel', 'Scheme')}:</span>{' '}
            {t('settings.gosi.legend.scheme', 'Legacy = hired before 3 Jul 2024 · NewPhased = new hires on the 2025–2028 ramp-up.')}
          </p>
          <p>
            <span className="font-medium text-foreground">{t('settings.gosi.legend.ratesLabel', 'Rates')}:</span>{' '}
            {t('settings.gosi.legend.rates', 'Employer/employee rates apply to basic + housing, capped at the wage cap (SAR 45,000). Leave “Effective to” blank for the current, open-ended rate.')}
          </p>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="text-start py-2 font-medium">{t('settings.gosi.fields.year')}</th>
                  <th className="text-start py-2 font-medium">{t('settings.gosi.fields.scheme')}</th>
                  <th className="text-start py-2 font-medium">{t('settings.gosi.fields.branchType')}</th>
                  <th className="text-end py-2 font-medium">{t('settings.gosi.fields.employerRate')}</th>
                  <th className="text-end py-2 font-medium">{t('settings.gosi.fields.employeeRate')}</th>
                  <th className="text-end py-2 font-medium">{t('settings.gosi.fields.wageCap')}</th>
                  <th className="text-start py-2 font-medium">{t('settings.gosi.fields.effectiveFrom')}</th>
                  <th className="text-start py-2 font-medium">{t('settings.gosi.fields.effectiveTo')}</th>
                  {isOperator && <th />}
                </tr>
              </thead>
              <tbody>
                {rates.map(r => (
                  <tr key={r.id} className="border-b border-border last:border-0">
                    <td className="py-2">{r.year}</td>
                    <td className="py-2">{r.scheme}</td>
                    <td className="py-2">{r.branchType}</td>
                    <td className="py-2 text-end font-mono">{(r.employerRate * 100).toFixed(2)}%</td>
                    <td className="py-2 text-end font-mono">{(r.employeeRate * 100).toFixed(2)}%</td>
                    <td className="py-2 text-end font-mono">{r.wageCap.toLocaleString('en-US')}</td>
                    <td className="py-2">{appDate(r.effectiveFrom)}</td>
                    <td className="py-2">{r.effectiveTo ? appDate(r.effectiveTo) : '—'}</td>
                    {isOperator && (
                      <td className="py-2 text-end">
                        <button
                          type="button"
                          onClick={() => setEditing(r)}
                          className="text-xs underline me-2"
                        >
                          {t('Edit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleArchive(r.id)}
                          className="text-xs underline text-red-600"
                        >
                          {t('settings.gosi.archive.confirmAction')}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(editing || creating) && (
          <GosiRateEditDialog
            row={editing}
            onClose={() => {
              setEditing(null);
              setCreating(false);
            }}
            onSaved={async () => {
              setEditing(null);
              setCreating(false);
              await refresh();
            }}
            onError={onError}
          />
        )}
      </CardContent>
    </Card>
  );
};

interface GosiRateEditDialogProps {
  row: GosiRateScheduleView | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onError: (msg: string) => void;
}

const GosiRateEditDialog: React.FC<GosiRateEditDialogProps> = ({ row, onClose, onSaved, onError }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<GosiRateScheduleInput>({
    id: row?.id,
    year: row?.year ?? new Date().getFullYear(),
    scheme: (row?.scheme as GosiScheme) ?? 'NewPhased',
    branchType: (row?.branchType as GosiBranchType) ?? 'ANI',
    employerRate: row?.employerRate ?? 0.10,
    employeeRate: row?.employeeRate ?? 0.10,
    wageCap: row?.wageCap ?? 45000,
    effectiveFrom: row?.effectiveFrom
      ? row.effectiveFrom.slice(0, 10)
      : todayLocalDateParam(),
    effectiveTo: row?.effectiveTo ? row.effectiveTo.slice(0, 10) : null,
  });
  const [saving, setSaving] = useState(false);
  // Rates are stored as fractions (0.105) but entered/displayed as percentages (10.5)
  // so the "%" labels are honest. Held as strings so typing "10." doesn't get mangled.
  const [employerPct, setEmployerPct] = useState<string>(
    row?.employerRate != null ? String(+(row.employerRate * 100).toFixed(4)) : '10'
  );
  const [employeePct, setEmployeePct] = useState<string>(
    row?.employeeRate != null ? String(+(row.employeeRate * 100).toFixed(4)) : '10'
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload: GosiRateScheduleInput = {
        ...form,
        employerRate: (parseFloat(employerPct) || 0) / 100,
        employeeRate: (parseFloat(employeePct) || 0) / 100,
      };
      const result = row?.id
        ? await updateGosiRate(row.id, payload)
        : await createGosiRate(payload);
      if (result.errors.length === 0) {
        await onSaved();
      } else {
        onError(getApiErrorMessage(result, t, t('common.error', 'An error occurred.')));
      }
    } catch (e) {
      onError(getApiErrorMessage(e, t, t('common.error', 'An error occurred.')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border p-5 rounded-xl shadow-xl w-[520px] max-w-[90vw] z-50">
          <Dialog.Title className="text-sm font-semibold mb-3">
            {t('settings.gosi.editRowTitle')}
          </Dialog.Title>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{t('settings.gosi.fields.year')}</span>
              <Input
                type="number"
                value={form.year}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, year: parseInt(e.target.value, 10) || 0 })}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{t('settings.gosi.fields.scheme')}</span>
              <select
                value={form.scheme}
                onChange={(e) => setForm({ ...form, scheme: e.target.value as GosiScheme })}
                className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="Legacy">{t('settings.gosi.schemes.Legacy', 'Legacy')}</option>
                <option value="NewPhased">{t('settings.gosi.schemes.NewPhased', 'NewPhased')}</option>
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{t('settings.gosi.fields.branchType')}</span>
              <select
                value={form.branchType}
                onChange={(e) => setForm({ ...form, branchType: e.target.value as GosiBranchType })}
                className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="ANI">ANI</option>
                <option value="OH">OH</option>
                <option value="SANED">SANED</option>
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{t('settings.gosi.fields.wageCap')}</span>
              <Input
                type="number"
                value={form.wageCap}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, wageCap: parseFloat(e.target.value) || 0 })}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{t('settings.gosi.fields.employerRate')}</span>
              <Input
                type="number"
                step="0.01"
                value={employerPct}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmployerPct(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{t('settings.gosi.fields.employeeRate')}</span>
              <Input
                type="number"
                step="0.01"
                value={employeePct}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmployeePct(e.target.value)}
              />
            </label>
            <p className="col-span-2 -mt-1 text-[11px] text-muted-foreground">
              {t('settings.gosi.rateHint', 'Enter rates as percentages — e.g. type 10.5 for 10.5%. Leave “Effective to” blank for the current open-ended rate.')}
            </p>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{t('settings.gosi.fields.effectiveFrom')}</span>
              <DateField
                value={form.effectiveFrom ? form.effectiveFrom.slice(0, 10) : ''}
                onChange={(v) => setForm({ ...form, effectiveFrom: v })}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{t('settings.gosi.fields.effectiveTo')}</span>
              <DateField
                value={form.effectiveTo ? form.effectiveTo.slice(0, 10) : ''}
                onChange={(v) => setForm({ ...form, effectiveTo: v || null })}
              />
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? t('common.saving', 'Saving...') : t('Save')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

